import React from 'react';
import { ArrowRightIcon, PlusIcon, EqualIcon } from 'lucide-react';
import { ENTERPRISE_ARCHITECTURE, ENTERPRISE_STEPS } from '../data/platform';
import { BOOK_DEMO_URL, GET_STARTED_URL } from '../data/dgpt';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { PageHero } from '../components/platform/PageHero';
import { CtaButton } from '../components/ui/CtaButton';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { CapabilityGrid } from '../components/platform/CapabilityGrid';
import { WorkflowBuilder } from '../components/platform/WorkflowBuilder/WorkflowBuilder';
import { HumanControl } from '../components/platform/HumanControl';
import { ProcessCta } from '../components/platform/ProcessCta';
function ArchitectureFigure() {
  return <figure aria-label="Your data, criteria and workflow plus DiligenceGPT agents equals your due process" className="rounded-2xl border border-night-line bg-night-panel p-5 sm:p-6" data-id="element-1853" data-mp="xb3bllge4n67">
      <ol className="flex flex-col gap-2" data-id="element-1854" data-mp="hnhyescj5dnd">
        {ENTERPRISE_ARCHITECTURE.map((item, index) => {
        const last = index === ENTERPRISE_ARCHITECTURE.length - 1;
        return <li key={item} className="flex flex-col items-center gap-2" data-id="element-1855" data-mp="8mtmq4alzpdn" data-mpi={index}>
              <div className={last ? 'w-full rounded-xl border border-violet/50 bg-violet/15 px-4 py-3.5 text-center text-sm font-semibold uppercase tracking-[0.1em] text-violet-ink' : 'w-full rounded-xl border border-night-line bg-night-2 px-4 py-3.5 text-center text-sm font-semibold uppercase tracking-[0.1em] text-night-text'} data-id="element-1856" data-mp="3p1u18253z8e">
                {item}
              </div>
              <span className="text-night-faint" aria-hidden="true" data-id="element-1857" data-mp="3h1v9zeytff7l">
                {last ? <EqualIcon className="h-4 w-4" data-id="element-1858" data-mp="3c3tmmhucwtqb" /> : <PlusIcon className="h-4 w-4" data-id="element-1859" data-mp="375s6o5s89aj2" />}
              </span>
            </li>;
      })}
      </ol>
      <div className="mt-2 rounded-xl bg-violet px-4 py-4 text-center text-sm font-semibold uppercase tracking-[0.14em] text-white" data-id="element-1860" data-mp="2xhd1ydfsfvxr">Your Due Process</div>
    </figure>;
}
export function Enterprise() {
  usePageMeta({
    title: 'DiligenceGPT™ Enterprise | Build Your Organization’s Due Process',
    description: 'Configure DiligenceGPT™ agents, workflows, requirements, approvals and reporting around the way your organization already operates. Custom agents, criteria, templates and permissions.',
    path: '/enterprise',
    keywords: ['enterprise due diligence platform', 'custom diligence workflows', 'procurement workflow automation', 'agentic workflow builder'],
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Enterprise',
        item: canonicalUrl('/enterprise')
      }]
    }]
  });
  return <main id="main" data-id="element-1861" data-mp="1sigb5ll8mttp">
      <PageHero headingId="enterprise-page-heading" breadcrumb="Enterprise" eyebrow="Enterprise" title="Build Your Organization’s Due Process on DiligenceGPT." description="Configure agents, workflows, requirements, approvals and reporting around the way your organization already operates." actions={<>
            <CtaButton href={BOOK_DEMO_URL} variant="violet" size="xl" event="enterprise_demo_click" eventParams={{
        placement: 'enterprise_hero'
      }} className="w-full sm:w-auto" data-id="element-1863" data-mp="19jlsi1inl989">
              Book a Demo
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1864" data-mp="1eip09tq0ptjx" />
            </CtaButton>
            <CtaButton href={GET_STARTED_URL} variant="outlineDark" size="xl" event="primary_cta_click" eventParams={{
        placement: 'enterprise_hero',
        cta: 'build_workflow'
      }} className="w-full sm:w-auto" data-id="element-1865" data-mp="2tzp687rn4fjy">
              Build Your Workflow
            </CtaButton>
          </>} aside={<ArchitectureFigure data-id="element-1866" data-mp="3u98bkyo57kph" />} data-id="element-1862" data-mp="1shoo4z5pi4f1" />

      <section aria-labelledby="capabilities-heading" className="border-b border-hairline-soft bg-surface" data-id="element-1867" data-mp="3u86wmj40ae2h">
        <div className="mx-auto w-full max-w-[1320px] px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-id="element-1868" data-mp="33qc7p38ij9f">
          <SectionHeading eyebrow="Capabilities" headingId="capabilities-heading" title="Your Rules. Your Data. Your Process." data-id="element-1869" data-mp="80yurqsaopb7" />
          <div className="mt-12" data-id="element-1870" data-mp="c1siok5qm443">
            <CapabilityGrid data-id="element-1871" data-mp="gulrbj4btwlg" />
          </div>
        </div>
      </section>

      <section aria-labelledby="enterprise-builder-heading" className="border-b border-hairline-soft bg-ink" data-id="element-1872" data-mp="gwodtt69t7lf">
        <div className="mx-auto w-full max-w-[1320px] px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-id="element-1873" data-mp="qsi0449skfge">
          <SectionHeading eyebrow="Workflow builder" headingId="enterprise-builder-heading" title="Turn Your Existing Process Into an Agentic Workflow." description="Each stage can carry agents, requirements, human reviewers, approvals, documents and conditions." data-id="element-1874" data-mp="qqorijfmbl5r" />
          <Reveal className="mt-12" data-id="element-1875" data-mp="26axqfnryb5ue">
            <WorkflowBuilder data-id="element-1876" data-mp="26bzf4v1w3qul" />
          </Reveal>
        </div>
      </section>

      <HumanControl placement="enterprise" data-id="element-1877" data-mp="21d5wko7qx9nk" />

      <section aria-labelledby="implementation-heading" className="border-b border-hairline-soft bg-surface" data-id="element-1878" data-mp="21eh5t3jyn9m2">
        <div className="mx-auto w-full max-w-[1320px] px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-id="element-1879" data-mp="2z1zdsc5ss7p0">
          <SectionHeading eyebrow="Implementation" headingId="implementation-heading" title="Start with one process. Extend to the next." data-id="element-1880" data-mp="2u3ea22rc75vt" />
          <ol className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-id="element-1881" data-mp="2kc0g0ai5mya2">
            {ENTERPRISE_STEPS.map((step, index) => <Reveal key={step.title} as="li" delay={index * 0.05} className="rounded-2xl border border-hairline bg-panel p-6" data-id="element-1882" data-mp="3hs77k3nlxlsj" data-mpi={index}>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-lime font-mono text-sm font-semibold text-white" data-id="element-1883" data-mp="3cy2z1fyp15pc">{index + 1}</span>
                <h3 className="mt-5 text-lg font-semibold tracking-[-0.015em] text-text-primary" data-id="element-1884" data-mp="1g05gducw856h">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary" data-id="element-1885" data-mp="11c13j70iunvm">{step.body}</p>
              </Reveal>)}
          </ol>
        </div>
      </section>

      <ProcessCta placement="enterprise" primaryLabel="Build Your Workflow" data-id="element-1886" data-mp="sat8cxtay35r" />
    </main>;
}